import { RootState } from '../store';
import { ApplicationStatus, LoanState } from '../../const';
import {
  IPaymentSubscription,
  PaymentSubscriptionStatus,
  UserFeedbackStatus,
} from 'redux/reducers/userApplications/type';
import { UserState } from '../reducers/user/type';

export const getApplicationStateSelector = (state: RootState) =>
  state.userApplications.applicationActive?.state;
export const getLoanStateSelector = (state: RootState) =>
  state.userApplications.loanActive && state.userApplications.loanActive.state;
const getIsMatchesLoanAndApplicationId = (state: RootState) =>
  state.userApplications.loanActive?.application_id ===
  state.userApplications.applicationActive?.id;
export const getLoanHistorySelector = (state: RootState) => state.userApplications.loanHistory;

export const getIsLoanActiveSelector = (state: RootState) => !!state.userApplications.loanActive;

export const selectIsUserExist = (state: RootState) => !!state.user.userInfo.id;
export const selectLoanHistorySelector = (state: RootState) => state.userApplications.loanHistory;
export const selectMoratoriumTill = (state: RootState) => state.user.userInfo?.moratorium_till;
export const selectFullListOfBanks = (state: RootState) => state.userApplications.fullListOfBanks;
export const selectFinbitRequest = (state: RootState) => state.userApplications.finbitRequest;
export const selectUserInfo = (state: RootState) => state.user.userInfo;
export const selectPriceList = (state: RootState) => state.priceList.priceList;
export const selectUserID = (state: RootState) => state.user.userInfo?.id;
export const selectIsUserActive = (state: RootState) => state.user.userInfo?.state === 'active';
export const selectIsUserConfirmed = (state: RootState) =>
  state.user.userInfo?.state === 'confirmed';
export const getForensicsRequestProcessed = (state: RootState) =>
  state.user.isForensicsRequestProcessed;
export const getForensicsRequestProcessing = (state: RootState) =>
  state.user.isForensicsRequestProcessing;
export const getUserBlackList = (state: RootState) => state.user.userInfo?.is_in_black_list;
export const getUserApplicationsSelector = (state: RootState) => state.userApplications;
export const getMoratoriumTillSelector = (state: RootState) => state.user.userInfo?.moratorium_till;

export const getFullListOfBanks = (state: RootState) => state.userApplications?.fullListOfBanks;
export const getFinbitRequestSelector = (state: RootState) => state.userApplications?.finbitRequest;

export const getIsRepeatLoan = (state: RootState) => {
  return getLoanHistorySelector(state).some((loan: any) => loan.state === LoanState.CLOSED);
};

export const getIsRepeatFirstLoan = (state: RootState) => {
  // if exist some applications, but no loans
  return !getIsRepeatLoan(state) && state?.userApplications?.applications.length > 1;
};

export const getIsFirstApplicationNoLoans = (state: RootState) => {
  // if exist only one application, but no loans
  return !getIsRepeatLoan(state) && state?.userApplications?.applications.length === 1;
};

export const getIsFirstLoan = (state: RootState) => {
  return state.userApplications.loanHistory.length === 0;
};

export const getPaymentSubscriptionSelector = (state: RootState) =>
  state.userApplications.paymentSubscription;
export const getFeedbackStatusSelector = (state: RootState) =>
  state.userApplications?.summary?.user_feedback.user_feedback_status;

export const getIsPendingApplicationSelector = (state: RootState) => {
  const status = getApplicationStateSelector(state);

  return status === ApplicationStatus.PENDING || status === ApplicationStatus.PROCESSING;
};

export const getIsApplicationApproved = (state: RootState) => {
  const status = getApplicationStateSelector(state);

  const isApplicationApply = getFeedbackStatusSelector(state);

  return status === ApplicationStatus.APPROVED && isApplicationApply !== UserFeedbackStatus.APPLY;
};

export const getIsAlternateEsignFlow = (state: RootState) =>
  state.userApplications.applicationActive?.alternative_esign_flow;

export const getIsSanctionLetterSigned = (state: RootState) =>
  state.userApplications.applicationAgreements &&
  state.userApplications.applicationAgreements?.some(
    (item) => item?.template.includes('sanction_letter') && !!item?.signed_document?.id
  );

export const getIsAgreementSigned = (state: RootState) =>
  state.userApplications.applicationAgreements &&
  state.userApplications.applicationAgreements?.some(
    (item) => item?.template.includes('agreement') && !!item?.signed_document?.id
  );

export const getIsAllDocumentsSigned = (state: RootState) =>
  getIsSanctionLetterSigned(state) && getIsAgreementSigned(state);

export const getIsSignAgreementWithZoopOne = (state: RootState) =>
  state.userApplications.applicationAgreements &&
  state.userApplications.applicationAgreements?.some(
    (item) => item?.signing_method.includes('ZOOP') && !item?.signed_document?.id
  );

export const getIsSignAgreementWithZoopOneCompleted = (state: RootState) =>
  state.userApplications.applicationAgreements?.some(
    (item) => item?.signing_method.includes('ZOOP') && !!item?.signed_document?.id
  );

export const getIsAutoDebitApplicationSelector = (state: RootState) => {
  const paymentSubscription = getPaymentSubscriptionSelector(state) || ({} as IPaymentSubscription);

  const status = getApplicationStateSelector(state);
  const paymentSubscriptionStatus = paymentSubscription?.status;

  return (
    status === ApplicationStatus.APPROVED &&
    paymentSubscriptionStatus !== PaymentSubscriptionStatus.BANK_APPROVAL_PENDING &&
    paymentSubscriptionStatus !== PaymentSubscriptionStatus.SKIPPED &&
    paymentSubscriptionStatus !== PaymentSubscriptionStatus.ACTIVE
  );
};

export const getIsContractSigningSelector = (state: RootState) => {
  const paymentSubscription = getPaymentSubscriptionSelector(state) || ({} as IPaymentSubscription);

  const paymentSubscriptionStatus = paymentSubscription?.status;

  const status = getApplicationStateSelector(state);

  return (
    status === ApplicationStatus.APPROVED &&
    (paymentSubscriptionStatus === PaymentSubscriptionStatus.BANK_APPROVAL_PENDING ||
      paymentSubscriptionStatus === PaymentSubscriptionStatus.SKIPPED ||
      paymentSubscriptionStatus === PaymentSubscriptionStatus.ACTIVE)
  );
};

export const getIsMoneyOnWaySelector = (state: RootState) => {
  const applicationStatus = getApplicationStateSelector(state);
  const loanStatus = getLoanStateSelector(state);

  return applicationStatus === ApplicationStatus.CONFIRMED && loanStatus === LoanState.PENDING;
};

export const getIsLoanSelector = (state: RootState) => {
  const loanState = getLoanStateSelector(state);

  return loanState === LoanState.ACTIVE || loanState === LoanState.DEFAULTED;
};

export const getIsApplicationActiveSelector = (state: RootState) =>
  !!state.userApplications.applicationActive;

export const getIsMoratoriumSelector = (state: RootState) => {
  const moratoriumTill = getMoratoriumTillSelector(state);

  if (!moratoriumTill) return false;

  return new Date(moratoriumTill) > new Date();
};

export const getIsRejectedWithoutMoratoriumSelector = (state: RootState) => {
  const isMoratoriumExist = !!getMoratoriumTillSelector(state);

  const isApplicationRejected = getApplicationStateSelector(state) === ApplicationStatus.REJECTED;

  return !isMoratoriumExist && isApplicationRejected;
};

export const getIsBlackList = (state: RootState) => {
  const loanState = getLoanStateSelector(state);

  return loanState !== LoanState.ACTIVE && !!getUserBlackList(state);
};

export const getIsRepeatLoanSelector = (state: RootState) => {
  const loanHistoryState = getLoanHistorySelector(state);

  return loanHistoryState.some((loan) => loan.state === LoanState.CLOSED);
};

export const selectPaymentLink = (state: RootState) =>
  state.userApplications.loanActive?.payment_link;
export const selectAccruedTotal = (state: RootState) =>
  state?.userApplications?.loanActive?.accrued_total;
export const selectProductId = (state: RootState) =>
  state?.userApplications?.loanActive?.product_id;
export const selectApplicationId = (state: RootState) =>
  state.userApplications?.applicationActive?.id;
export const selectDaysPastDue = (state: RootState) =>
  state?.userApplications?.loanActive?.days_past_due;
export const selectLoanActiveId = (state: RootState) => state.userApplications.loanActive?.id;
export const selectLoanDisbursed = (state: RootState) =>
  state.userApplications.loanActive?.disbursed_at;
export const selectLoanMatured = (state: RootState) =>
  state.userApplications.loanActive?.matured_at;
export const selectLoanInstallmentsActiveInfo = (state: RootState) =>
  state.userApplications.loanInstallmentsActiveInfo;
export const selectLoanActive = (state: RootState) => state.userApplications.loanActive;
export const selectNextPayment = (state: RootState) =>
  state?.userApplications?.paymentPlan?.actual_payment_plan?.next_payment;
export const selectSettleTotal = (state: RootState) =>
  state?.userApplications?.paymentPlan?.expected_payment_plan?.discounted_settlement_amount;
export const selectFullPayment = (state: RootState) =>
  state?.userApplications?.paymentPlan?.actual_payment_plan?.total_unpaid;
export const selectFullAccrued = (state: RootState) =>
  state?.userApplications?.paymentPlan?.actual_payment_plan?.total_accrued;
export const selectFullPaid = (state: RootState) =>
  state?.userApplications?.paymentPlan?.actual_payment_plan?.total_paid;
export const selectInstallments = (state: RootState) =>
  state?.userApplications?.paymentPlan?.expected_payment_plan?.installments;
export const selectActualInstallments = (state: RootState) =>
  state?.userApplications?.paymentPlan?.actual_payment_plan?.installments;
export const selectForPDLInstallments = (state: RootState) =>
  state?.userApplications?.paymentPlan?.actual_payment_plan?.installments[0]
    ?.scheduled_accrued_fee_data;
export const selectProductType = (state: RootState) =>
  state?.userApplications?.paymentPlan?.product;

export const selectVerifyBankIFCS = (state: RootState) => state?.user?.userInfo?.ifsc_code;

export const selectVerifyBankAccount = (state: RootState) =>
  state?.user?.userInfo?.bank_account_number;

export const selectIsCreditBureauConsentGiven = (state: RootState) =>
  !!state.user.userInfo.is_credit_bureau_consent_given;

export const selectIsUserStateActive = (state: RootState) =>
  state.user.userInfo.state === UserState.ACTIVE;

export const selectIsUserBlocked = (state: RootState) =>
  state.user.userInfo.state === UserState.BLOCKED;

export const selectIsUserBlockedBySoldLoan = (state: RootState) =>
  state.user.userInfo.block_type === 'sold_loan';

export const selectUserSoldLoanBuyerMessage = (state: RootState) =>
  state.user.userInfo.sold_loan_buyer_message;

export const selectPreferableLanguages = (state: RootState) => state.user.preferable_languages;

export const selectIsRepeatLoanSelector = (state: RootState) => {
  const loanHistoryState = selectLoanHistorySelector(state);

  return loanHistoryState.some((loan: any) => loan.state === LoanState.CLOSED);
};

export const selectCustomPaymentInfoTotalUnpaid = (state: RootState) =>
  state?.userApplications?.customPaymentInfo?.total_unpaid;

export const selectCustomPaymentInfoNextPaymentTotal = (state: RootState) =>
  state?.userApplications?.customPaymentInfo?.next_payment_total;

export const selectCustomPaymentInfoDaysPastDue = (state: RootState) =>
  state?.userApplications?.customPaymentInfo?.loan.days_past_due;

export const selectCustomPaymentInfoMaturedAt = (state: RootState) =>
  state?.userApplications?.customPaymentInfo?.loan.matured_at;

export const getIsShowCalculatorSelector = (state: RootState) => {
  const applicationStatus = getApplicationStateSelector(state);
  const loanStatus = getLoanStateSelector(state);

  return (
    applicationStatus === ApplicationStatus.REJECTED ||
    applicationStatus === ApplicationStatus.CANCELLED ||
    (getIsMatchesLoanAndApplicationId(state) &&
      applicationStatus === ApplicationStatus.CONFIRMED &&
      loanStatus === LoanState.CLOSED) ||
    (getIsMatchesLoanAndApplicationId(state) &&
      applicationStatus === ApplicationStatus.CONFIRMED &&
      loanStatus === LoanState.CANCELLED)
  );
};
export const selectLoanHistory = (state: RootState) => state.userApplications.loanHistory;

export const selectAppSettings = (state: RootState) => state.user.appSetting;

export const selectAadhaarVerificationInfo = (state: RootState) =>
  state.user.aadhaarVerificationInfo;
